import { differenceInDays, format } from 'date-fns';
import { INextArticle, IOrder, getLocalOptionsFromAllOptions } from 'next-common';
import { ArticleType, IPcodeMaster, IVehicleStockEcomConfiguration, IVehicleStockEcomItem } from 'sales-common';
import { prepareVehicleConfig, removeDuplicateObjectFromArray, VehicleConfig } from './vehicleOrder';
import { ICartSummaryData } from '@next-components/cart';
import { prepareArticles } from './vat';
import { defaultTo, differenceBy } from 'lodash';
import { isOfferCase } from './order';

export interface ICurrentOptions {
  name: string;
  price: number;
  sku: string;
}

export interface ICurrentChanges {
  addedOptions: ICurrentOptions[];
  removedOptions: ICurrentOptions[];
}

export const applyTTCodeValidation = (pCode: IPcodeMaster, vehicleTTCode: number, order: IOrder): [boolean, boolean] => {
  if ((!pCode?.trackTraceAdd && !pCode?.trackTraceRemove) || !vehicleTTCode) return [true, true];

  let isSelectionAllowed = false;
  let isDeSelectionAllowed = false;

  // Options cannot be added after vehicle has reached TT-limit
  if (!pCode?.trackTraceAdd || (pCode?.trackTraceAdd && pCode?.trackTraceAdd >= vehicleTTCode)) {
    isSelectionAllowed = true;
  }

  // Options cannot be removed after vehicle has reached TT-limit
  // validation will work only for offers not orders
  if(isOfferCase(order)) {
    isDeSelectionAllowed = true;
  }
  else if (!pCode?.trackTraceRemove || (pCode?.trackTraceRemove && pCode?.trackTraceRemove >= vehicleTTCode)) {
    isDeSelectionAllowed = true;
  }

  // console.log("TT Code validation: ", pCode?.localOptionCode, ":", `isSelectionAllowed: ${isSelectionAllowed}, isDeSelectionAllowed: ${isDeSelectionAllowed}`)
  return [isSelectionAllowed, isDeSelectionAllowed];
};

export const isDateValidateFailed = (pCode: IPcodeMaster): [boolean, boolean] => {
  try {
    let isValidaFomDate = true;
    let isValidToDate = true;
    const currentDate = new Date(format(new Date(), 'yyyy-MM-dd'));

    //Valid From Date validation
    if (pCode?.validFromDate) {
      const validFromDate = new Date(format(new Date(pCode?.validFromDate), 'yyyy-MM-dd'));
      const days = differenceInDays(validFromDate, currentDate);
      isValidaFomDate = days <= 0;
    }

    //Valid To Date validation
    if (pCode?.validToDate) {
      const validToDate = new Date(format(new Date(pCode?.validToDate), 'yyyy-MM-dd'));
      const days = differenceInDays(validToDate, currentDate);
      isValidToDate = days >= 0;
    }

    return [isValidaFomDate, isValidToDate];
  } catch (error) {
    console.log('Error in isDateValidateFailed', error);
    return [true, true];
  }
};

export const vehicleStatusToSavedDataValidation = (order: IOrder, vehicleData: IVehicleStockEcomItem) => {
  const localOptions = getLocalOptionsFromAllOptions(vehicleData?.vehicleConfiguration?.options);
  const mismatchedData = order?.pCodes?.filter((pcode) => {
    const pCodeFound = localOptions?.filter((data) => data?.optionId === pcode?.localOptionCode);

    return pCodeFound?.length ? false : true;
  });

  console.log('Vehicle status to saved data mis-match =>', mismatchedData);

  return mismatchedData;
};

export const masterAPIToSavedDataValidation = (order: IOrder, masterData: IPcodeMaster[]) => {
  if (!order?.pCodes?.length) return [];
  const mismatchedData = order?.pCodes?.filter((pcode) => {
    const pCodeFound = masterData?.filter((data) => data?.localOptionCode === pcode?.localOptionCode);

    return pCodeFound?.length ? false : true;
  });

  console.log('Master API to saved data mis-match =>', mismatchedData);
  return mismatchedData;
};

export const getPriceValidation = (order: IOrder, removedOptions: IPcodeMaster[], masterData: IPcodeMaster[]) => {
  if (!order?.pCodes?.length) return [];
  const priceMismatchPcodes = [];
  const filteredPcodes = differenceBy(order?.pCodes, removedOptions, 'localOptionCode');
  filteredPcodes?.forEach((pcode) => {
    const masterPcode = masterData?.find((option) => option?.localOptionCode === pcode?.localOptionCode);
    const currentPrice = defaultTo(pcode?.customerPriceExclVat, 0);
    const newPrice = defaultTo(masterPcode?.customerPriceExclVat, 0);
    if (currentPrice !== newPrice) {
      priceMismatchPcodes?.push({
        name: pcode?.localOptionDescription,
        currentPrice,
        newPrice,
      });
    }
  });
  return priceMismatchPcodes;
};

export const mapSelectedOptions = (pCodes: IPcodeMaster[]) => {
  if (!pCodes?.length) return [];
  return pCodes?.map((pcode) => {
    return {
      name: defaultTo(pcode?.localOptionDescription, ''),
      price: defaultTo(pcode?.customerPriceExclVat, 0),
      sku: defaultTo(pcode?.localOptionCode, ''),
    };
  });
};

export const setOptionChanges = (
  existingOption: ICurrentChanges,
  addedOption: ICurrentOptions[],
  removedOption: ICurrentOptions[],
): ICurrentChanges => {
  const addedPcodes = [...existingOption?.addedOptions, ...addedOption];
  const removedPcodes = [...existingOption?.removedOptions, ...removedOption];
  const addedOptions = removeDuplicateObjectFromArray(addedPcodes, 'sku');
  const removedOptions = removeDuplicateObjectFromArray(removedPcodes, 'sku');
  return { addedOptions, removedOptions };
};

export const mapPCodes = (
  vehiclePcdeos: IVehicleStockEcomConfiguration,
  masterData: IPcodeMaster[],
): IPcodeMaster[] => {
  if (!vehiclePcdeos?.options?.length) return [];
  const localOptions = getLocalOptionsFromAllOptions(vehiclePcdeos?.options);
  const mappedPcodes: IPcodeMaster[] = [];
  localOptions?.forEach((option) => {
    const masterPcode = masterData?.find((pcode) => pcode?.localOptionCode === option?.optionId);
    mappedPcodes.push(masterPcode);
  });
  return mappedPcodes;
};

export const getSelectedPcodes = (
  orderPcodes: IPcodeMaster[],
  selectedVehicleData: IVehicleStockEcomItem,
  masterData: IPcodeMaster[],
) => {
  const mappedVehicleStatusOptions = mapPCodes(selectedVehicleData?.vehicleConfiguration, masterData);
  const allPcodes = removeDuplicateObjectFromArray(
    [...(orderPcodes || []), ...(mappedVehicleStatusOptions || [])],
    'localOptionCode',
  );
  const mappedPcodes: IPcodeMaster[] = [];
  const articles: INextArticle[] = [];
  const cartData: ICartSummaryData[] = [];

  allPcodes?.forEach((option) => {
    const masterPcode = masterData?.find((pcode) => pcode?.localOptionCode === option?.localOptionCode);
    if (masterPcode) {
      mappedPcodes.push(masterPcode);
      const optionKey = `${VehicleConfig.PCODE}_${masterPcode?.localOptionCode}`;
      articles.push(
        ...prepareArticles(
          optionKey,
          masterPcode?.customerPriceExclVat,
          masterPcode?.vatCode,
          ArticleType.EQUIPMENT,
          masterPcode?.costPriceExclVat,
        ),
      );
      cartData?.push(
        prepareVehicleConfig(
          optionKey,
          masterPcode?.localOptionCode,
          masterPcode?.localOptionDescription,
          masterPcode?.customerPriceExclVat,
          masterPcode?.vatCode,
          false,
          masterPcode?.costPriceExclVat,
        ),
      );
    }
  });
  return { pCodes: mappedPcodes, mappedArticles: articles, cartData, preSelectedPcodes: mappedVehicleStatusOptions };
};
